import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

export const NOTIFICATION_CARD_FOCUS_DELAY_MS = 350;
export const NOTIFICATION_DIALOG_DELAY_MS = 1050;
export const NOTIFICATION_FOCUS_HOLD_MS = 1800;

export function idsMatch(a, b) {
    if (a == null || b == null || a === '') return false;
    return String(a) === String(b);
}

export function eventIdFromNotification(item) {
    if (!item || item.event == null) return null;
    if (typeof item.event === 'object') {
        return item.event._id != null ? String(item.event._id) : null;
    }
    return String(item.event);
}

export function hostIdFromArtistOfferKey(uniqueKey) {
    if (!uniqueKey || typeof uniqueKey !== 'string') return null;
    const parts = uniqueKey.split(':');
    if (parts[0] === 'artist_offer' && parts.length >= 4) {
        return parts[2] || null;
    }
    return null;
}

export function hatFromNotification(item) {
    if (item && item.hat) return item.hat;
    if (!item || !item.type) return '';
    if (
        item.type === 'HOST_NEARBY_EVENT' ||
        item.type === 'HOST_POST_EVENT_SURVEY'
    ) {
        return 'HOST';
    }
    if (
        item.type === 'ARTIST_HOST_OFFER' ||
        item.type === 'ARTIST_POST_EVENT_SURVEY'
    ) {
        return 'ARTIST';
    }
    if (
        item.type === 'ATTENDEE_EVENT_INVITE' ||
        item.type === 'ATTENDEE_INCOMPLETE_PROFILE'
    ) {
        return 'ATTENDEE';
    }
    return '';
}

export function notificationDestination(item) {
    if (!item) return '/dashboard';
    if (
        item.type === 'ATTENDEE_INCOMPLETE_PROFILE' ||
        (typeof item.href === 'string' &&
            item.href.startsWith('/edit-attendee-profile'))
    ) {
        return item.href || '/edit-attendee-profile';
    }
    const eventId = eventIdFromNotification(item);
    const hat = hatFromNotification(item);

    if (!eventId) {
        if (hat) {
            return `/dashboard?hat=${encodeURIComponent(hat)}`;
        }
        return item.href || '/dashboard';
    }

    const params = new URLSearchParams();
    if (hat) params.set('hat', hat);
    params.set('eventID', eventId);

    if (
        item.type === 'HOST_POST_EVENT_SURVEY' ||
        item.type === 'ARTIST_POST_EVENT_SURVEY'
    ) {
        params.set('open', 'survey');
    } else if (item.type === 'ARTIST_HOST_OFFER') {
        params.set('open', 'offer');
        const hostId = hostIdFromArtistOfferKey(item.uniqueKey);
        if (hostId) params.set('hostID', hostId);
    }

    return `/dashboard?${params.toString()}`;
}

export function useNotificationCardFocus(eventId, options) {
    const autoReleaseMs =
        options && typeof options.autoReleaseMs === 'number'
            ? options.autoReleaseMs
            : 0;
    const location = useLocation();
    const queryEventID = new URLSearchParams(location.search).get('eventID');
    const isTarget =
        eventId != null &&
        eventId !== '' &&
        queryEventID != null &&
        String(eventId) === String(queryEventID);
    const [focused, setFocused] = useState(false);

    useEffect(() => {
        if (!isTarget) {
            setFocused(false);
            return undefined;
        }
        const focusTimer = setTimeout(() => {
            setFocused(true);
        }, NOTIFICATION_CARD_FOCUS_DELAY_MS);
        let releaseTimer;
        if (autoReleaseMs > 0) {
            releaseTimer = setTimeout(() => {
                setFocused(false);
            }, NOTIFICATION_CARD_FOCUS_DELAY_MS + autoReleaseMs);
        }
        return () => {
            clearTimeout(focusTimer);
            if (releaseTimer) clearTimeout(releaseTimer);
        };
    }, [
        isTarget,
        autoReleaseMs,
        location.key,
        location.state,
        eventId,
    ]);

    const releaseFocus = () => setFocused(false);

    return { focused, releaseFocus, isTarget };
}
